// App.jsx – Combined working API + new features (zonal, expand chart, CSV export, access level)
// Updated to handle GeoJSON polygon from spatial filter modal
import { getProductConfig, getProductType } from "./config/productConfig";
import React, { useState, useCallback, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import moment from "moment-jalaali";
import SideMenu from "./components/SideMenu";
import MapSection from "./components/MapSection";
import Loading from "./components/Loading";
import Auth from "./components/Auth";
import TimeFilterDialog from "./components/TimeFilterDialog";
import { mapTimeSeriesResponse } from "./api/timeSeriesApi";
import { getAuthHeaders } from "./api/apiClient";
import Dashboard from "./Pages/Dashboard";
import Contact from "./Pages/Contact";
import About from "./Pages/About";
import UserManagement from "./Pages/UserManagement";
import UserProfile from "./Pages/UserProfile";
import Header from "./components/Header";
import "./App.css";

// ---------- Helper: fetch product list ----------
const fetchProductList = async (productType) => {
  const response = await fetch(
    `/api/products/list/?product_type=${productType}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    },
  );
  if (!response.ok)
    throw new Error(`Failed to fetch products: ${response.status}`);
  const data = await response.json();
  if (!Array.isArray(data) || data.length === 0)
    throw new Error(`No products found for ${productType}`);
  return data;
};

// ---------- Zonal (Polygon) API ----------
const fetchZonalTimeSeries = async (
  productId,
  geometry,
  dateFrom,
  dateTo,
  band = 1,
) => {
  const url = `/api/products/${productId}/timeseries/zonal/`;
  const body = { geometry, band };
  if (dateFrom) body.from = dateFrom;
  if (dateTo) body.to = dateTo;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || `Error ${response.status} fetching zonal time series`,
    );
  }
  return response.json();
};

// ---------- Point time series ----------
const fetchPointTimeSeries = async (productId, lon, lat, dateFrom, dateTo) => {
  const params = new URLSearchParams({ lon, lat, band: "1" });
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  const response = await fetch(
    `/api/products/${productId}/timeseries/?${params}`,
    {
      headers: getAuthHeaders(),
    },
  );
  if (!response.ok) throw new Error(`Failed to fetch point time series: ${response.status}`);
  // Return parsed JSON as-is; caller will attempt to map permissively to the UI.
  const data = await response.json().catch(() => null);
  return data;
};

// ---------- Main AppContent ----------
const AppContent = ({ onLogout }) => {
  const [isSideMenuOpen, setSideMenuOpen] = useState(true);
  const [isBottomMenuOpen, setIsBottomMenuOpen] = useState(false);
  const [isTimeFilterOpen, setIsTimeFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [activeIndicator, setActiveIndicator] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [spatialSelection, setSpatialSelection] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [timeSeriesError, setTimeSeriesError] = useState(null);
  const [isOverlayLoading, setIsOverlayLoading] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [productConfig, setProductConfig] = useState(null);
  const [legendData, setLegendData] = useState(null);
  const [visualizationParams, setVisualizationParams] = useState(null);

  // User access level
  const [userAccessLevel, setUserAccessLevel] = useState("basic");
  useEffect(() => {
    const fetchAccessLevel = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      try {
        const res = await fetch("/api/access-level/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUserAccessLevel(data.access_level || "basic");
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchAccessLevel();
  }, []);

  // *** NEW STATE – holds the GeoJSON polygon returned by spatial filter ***
  const [filteredPolygon, setFilteredPolygon] = useState(null);

  const convertJalaliToISO = (jalaliMoment) => {
    if (!jalaliMoment || typeof jalaliMoment.format !== "function") return null;
    return jalaliMoment.locale("en").format("YYYY-MM-DD");
  };

  // Called when user selects an indicator from SideMenu
  const handleIndicatorSelect = useCallback(async (indicator) => {
    setActiveIndicator(indicator);
    setTimeSeriesData([]);
    setTimeSeriesError(null);
    setIsBottomMenuOpen(false);

    if (!indicator) {
      setSelectedProductType(null);
      setSelectedProductId(null);
      setProductConfig(null);
      setLegendData(null);
      setVisualizationParams(null);
      return;
    }

    const productType = getProductType(indicator);
    const config = getProductConfig(indicator);
    setProductConfig(config);
    setSelectedProductType(productType);

    if (!productType) {
      setTimeSeriesError("این شاخص هنوز به نوع محصول متصل نشده است.");
      return;
    }

    try {
      setIsOverlayLoading(true);
      const products = await fetchProductList(productType);
      const firstProduct = products[0];
      setSelectedProductId(firstProduct.id);
      setLegendData(null); // Let Legend.jsx fetch from /api/legends/{productType}/ directly
      setVisualizationParams({ opacity: 0.85 });
    } catch (error) {
      console.error(error);
      setTimeSeriesError(error.message);
      setSelectedProductId(null);
    } finally {
      setIsOverlayLoading(false);
    }
  }, []);

  const handlePixelSelectData = (selection) => {
    setSpatialSelection(selection);
    setTimeSeriesError(null);
  };

  // Runs after user selects date range
  const runTimeSeriesQuery = async (selection, start, end) => {
    if (!selectedProductId) {
      setTimeSeriesError("محصول فعال یافت نشد. دوباره شاخص را انتخاب کنید.");
      return;
    }
    const dateFrom = convertJalaliToISO(start);
    const dateTo = convertJalaliToISO(end);

    try {
      setIsOverlayLoading(true);
      setTimeSeriesError(null);

      if (selection.type === "polygon") {
        const polygonGeometry = {
          type: "Polygon",
          coordinates: [selection.coordinates],
        };
        const data = await fetchZonalTimeSeries(
          selectedProductId,
          polygonGeometry,
          dateFrom,
          dateTo,
        );
        try {
          setTimeSeriesData(mapTimeSeriesResponse(data));
          if (data?.unit) setProductConfig((p) => ({ ...(p || {}), unit: data.unit }));
        } catch (e) {
          // fallback: try to map permissively
          const pts = Array.isArray(data?.time_series) ? data.time_series : [];
          setTimeSeriesData(
            pts.map((item, idx) => ({
              id: item.product_id || idx,
              label: item.date || item.datetime || String(idx),
              value:
                item.value != null
                  ? Number(item.value)
                  : Number(item[item.index] ?? item[item.index?.toLowerCase()] ?? null),
            })),
          );
          if (data?.unit) setProductConfig((p) => ({ ...(p || {}), unit: data.unit }));
        }
      } else if (selection.type === "point") {
        const data = await fetchPointTimeSeries(
          selectedProductId,
          selection.lon,
          selection.lat,
          dateFrom,
          dateTo,
        );
        try {
          setTimeSeriesData(mapTimeSeriesResponse(data));
          if (data?.unit) setProductConfig((p) => ({ ...(p || {}), unit: data.unit }));
        } catch (e) {
          // Permissive mapping: accept several possible shapes
          const pts =
            Array.isArray(data?.time_series) ? data.time_series :
            Array.isArray(data?.timeSeries) ? data.timeSeries :
            Array.isArray(data?.results) ? data.results :
            Array.isArray(data) ? data :
            Array.isArray(data?.timeseries) ? data.timeseries : [];

          setTimeSeriesData(
            pts.map((item, idx) => ({
              id: item?.product_id ?? item?.id ?? idx,
              label:
                item?.date ?? item?.datetime ?? item?.time ?? item?.label ?? String(idx),
              value:
                item?.value != null
                  ? Number(item.value)
                  : item?.mean != null
                  ? Number(item.mean)
                  : item?.avg != null
                  ? Number(item.avg)
                  : null,
            })),
          );
          if (data?.unit) setProductConfig((p) => ({ ...(p || {}), unit: data.unit }));
        }
      } else {
        throw new Error("نوع انتخاب مکانی پشتیبانی نمی‌شود");
      }
    } catch (error) {
      console.error(error);
      setTimeSeriesError(error?.message || "خطا در دریافت سری زمانی");
      setTimeSeriesData([]);
    } finally {
      setIsOverlayLoading(false);
    }
  };

  const openTimeFilterDialog = () => {
    if (!selectedProductType || !activeIndicator) {
      setTimeSeriesError("لطفاً ابتدا یک شاخص انتخاب کنید.");
      return;
    }
    if (!spatialSelection) {
      setTimeSeriesError("لطفاً یک نقطه یا چندضلعی روی نقشه انتخاب کنید.");
      return;
    }
    setTimeSeriesError(null);
    setIsTimeFilterOpen(true);
  };

  const handleTimeFilterConfirm = async () => {
    setIsTimeFilterOpen(false);
    setIsBottomMenuOpen(true);
    await runTimeSeriesQuery(spatialSelection, startDate, endDate);
  };

  const handleTimeFilterClear = () => {
    setStartDate(null);
    setEndDate(null);
  };

  // *** UPDATED – receives GeoJSON FeatureCollection from SpatialFilterModal ***
  const handleSpatialFilterApplied = (geoJSON) => {
    if (!geoJSON) {
      // Clear filter (null passed)
      setFilteredPolygon(null);
      setSpatialSelection(null);
      setTimeSeriesError(null);
      return;
    }

    // Store the full GeoJSON for display on map
    setFilteredPolygon(geoJSON);

    // Extract the first polygon's coordinates for time series use
    const feature = geoJSON.features?.[0];
    if (feature && feature.geometry && feature.geometry.type === "Polygon") {
      const coordinates = feature.geometry.coordinates[0]; // ring of [lng, lat]
      setSpatialSelection({ type: "polygon", coordinates });
      setTimeSeriesError(null);
      setIsTimeFilterOpen(true); // automatically open date picker
    } else {
      setTimeSeriesError("ساختار چندضلعی معتبر نیست.");
      setSpatialSelection(null);
    }
  };

  return (
    <div className="App">
      <div className="main-container">
        {/* MapSection – now receives the filtered polygon for display */}
        <MapSection
          isSideMenuOpen={isSideMenuOpen}
          isBottomMenuOpen={isBottomMenuOpen}
          activeIndicator={activeIndicator}
          selectedProductId={selectedProductId}
          selectedProductType={selectedProductType}
          productConfig={productConfig}
          legendData={legendData}
          visualizationParams={visualizationParams}
          activeTool={activeTool}
          onPixelSelect={handlePixelSelectData}
          setActiveTool={setActiveTool}
          setOverlayLoading={setIsOverlayLoading}
          onRequestTimeFilter={openTimeFilterDialog}
          timeSeriesData={timeSeriesData}
          timeSeriesError={timeSeriesError}
          filteredPolygon={filteredPolygon} // <-- new prop
          onLogout={onLogout}               // <-- logout callback
        />
        {/* SideMenu – with access level */}
        <SideMenu
          isOpen={isSideMenuOpen}
          setIsOpen={setSideMenuOpen}
          isBottomMenuOpen={isBottomMenuOpen}
          onSelectIndicator={handleIndicatorSelect}
          onOpenTimeFilter={openTimeFilterDialog}
          onSpatialFilterApplied={handleSpatialFilterApplied}
          userAccessLevel={userAccessLevel}
        />
      </div>

      {isOverlayLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div className="loader" />
        </div>
      )}

      {timeSeriesError && (
        <div
          className="app-toast-error"
          role="alert"
          onClick={() => setTimeSeriesError(null)}
        >
          {timeSeriesError}
        </div>
      )}

      <TimeFilterDialog
        open={isTimeFilterOpen}
        onClose={() => setIsTimeFilterOpen(false)}
        onConfirm={handleTimeFilterConfirm}
        onClear={handleTimeFilterClear}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
    </div>
  );
};

const App = () => {
  // Initialize directly from localStorage so a page refresh doesn't lose the session.
  // We check for both token AND the user object written by Auth.jsx on successful login.
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return Boolean(
      localStorage.getItem("authToken") && localStorage.getItem("user"),
    );
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsLoggedIn(true);
    }, 2000);
  };

  // Clear all auth storage and return to the login screen.
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    setIsLoggedIn(false);
  };

  if (isLoading) return <Loading />;
  if (!isLoggedIn) return <Auth onLogin={handleLogin} />;

  return (
    <Routes>
      <Route path="/" element={<AppContent onLogout={handleLogout} />} />
      <Route path="/home" element={<AppContent onLogout={handleLogout} />} />
      <Route
        path="/dashboard"
        element={
          <div className="page-layout">
            <div className="page-header">
              <Header onLogout={handleLogout} />
            </div>
            <div className="page-content">
              <Dashboard />
            </div>
          </div>
        }
      />
      <Route
        path="/contact"
        element={
          <div className="page-layout">
            <div className="page-header">
              <Header onLogout={handleLogout} />
            </div>
            <div className="page-content">
              <Contact />
            </div>
          </div>
        }
      />
      <Route
        path="/about"
        element={
          <div className="page-layout">
            <div className="page-header">
              <Header onLogout={handleLogout} />
            </div>
            <div className="page-content">
              <About />
            </div>
          </div>
        }
      />
      <Route
        path="/admin/users"
        element={<UserManagement onLogout={handleLogout} />}
      />
      <Route
        path="/profile"
        element={<UserProfile onLogout={handleLogout} />}
      />
    </Routes>
  );
};

export default App;
